import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next, once } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type {
	APIData,
	DateRangeInterface,
	DSSetup,
	PopupData,
	RequestError,
	Service,
} from 'webapp-types';
import type { GoalTargetSuccessCondition } from 'webapp/enums/enums';
import {
	BillingFeature,
	GoalFormatType,
	GoalOwnerScheduleType,
	GoalProgressType,
	GoalTargetValueType,
	GoalType,
	TimeUnit,
	UserAccess,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import {
	fillValueRowsFromHistoryValues,
	getGoalRangeTimeUnitForPrimaryGranulation,
	getHistoryValuesFromValueRows,
	getShouldRecalculateAllValues,
	groupValueRowsByTimeUnit,
	recalculateAllValues,
} from 'webapp/utils/goal-value-rows';
import { getFormattedTime } from 'webapp/utils/number';

import willDestroy from 'webapp/modifiers/will-destroy';
import BarLoaderComponent from 'webapp/components/bar-loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormLeaveConfirmPopupComponent from 'webapp/components/form/leave-confirm/popup';
import FormMetricSelectComponent from 'webapp/components/form/metric-select/index';
import FormRadiobuttonBoxGroupComponent from 'webapp/components/form/radiobutton-box-group';
import type { BoxItemInfo } from 'webapp/components/form/radiobutton-box-group';
import FormSelectComponent from 'webapp/components/form/select';
import FormSelectAttributesComponent from 'webapp/components/form/select-attributes';
import FormSelectDimensionsComponent from 'webapp/components/form/select-dimensions';
import FormSelectUnitsComponent from 'webapp/components/form/select-units';
import GoalNotificationsOwnerComponent from 'webapp/components/goal/notifications/owner';
import GoalNotificationsSettingsComponent from 'webapp/components/goal/notifications/settings';
import GoalValueQueryComponent from 'webapp/components/goal/value-query';
import GoalsMeasurementDropdownComponent from 'webapp/components/goals/measurement/dropdown';
import GoalsStartingValueComponent from 'webapp/components/goals/starting-value';
import GoalsSuccessConditionComponent from 'webapp/components/goals/success-condition';
import GoalsTargetTypeAndGranulationDropdownComponent from 'webapp/components/goals/target-type-and-granulation/dropdown';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import RowErrorComponent from 'webapp/components/row-error';
import SubmitButtonComponent from 'webapp/components/submit-button';
import ToolTipComponent from 'webapp/components/tool-tip';

import DateRange from 'webapp/models-custom/date-range';
import type GoalValueRow from 'webapp/models-custom/goal/value-row';
import MetricSetup from 'webapp/models-custom/metric-setup';
import DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

import type { GoalRangeTimeUnit } from 'webapp/services/model-manager/goals';
import type { RSjqXHR } from 'webapp/services/request';

interface ProgressTypeItem extends BoxItemInfo {
	description: string; // shown below the box group
	showUpgrade?: boolean;
}

export default class GoalPopupEditComponent extends PopupInnerModalComponent<PopupData.Goal.Edit> {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('request') declare requestService: Service.Request;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/forecasts') declare mmForecastsService: Service.ModelManager.Forecasts;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('account') declare accountService: Service.Account;
	@service('popup') declare popupService: Service.Popup;
	@service('timeframe') declare timeframeService: Service.Timeframe;

	@tracked metricSetup: MetricSetup;
	@tracked areNotificationsEnabled = false;
	@tracked isTimeframePeriodsLoading = false;
	@tracked startValue!: string | null;
	@tracked forecastData: APIData.MetricInsights.ForecastDataResponse | null = null;
	@tracked isForecastFirstDayOfTheWeekError = false;
	@tracked hasTargetValueChanged = false;

	customDateFormat = DateRange.customDateFormat;
	visibilityList = [
		{
			value: UserAccess.PUBLIC,
			label: 'All users',
		},
		{
			value: UserAccess.RESTRICTED,
			label: 'Me and selected users',
		},
		{
			value: UserAccess.PRIVATE,
			label: 'Only me',
		},
	];

	decimalsOptions = [
		{
			value: 0,
			label: '0 decimals',
		},
		{
			value: 1,
			label: '1 decimal',
		},
		{
			value: 2,
			label: '2 decimals',
		},
		{
			value: 3,
			label: '3 decimals',
		},
		{
			value: 4,
			label: '4 decimals',
		},
	];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.checkToSetDefaultTargetValueOnPercentualChange();
		this.metricSetup = new MetricSetup(
			{
				key: this.model.mainMetricSetupKey,
				spaceAccessId: this.model.mainMetricSetupSpaceAccessId,
				dimensions: this.model.mainMetricSetupDimensions,
				filters: this.model.mainMetricSetupFilters,
				allowAllDimensionValues: false,
				allowAllUnits: false,
			},
			(prop, value) => {
				switch (prop) {
					case 'key':
						this.model.mainMetricSetupKey = value as string;
						this.checkToSetDefaultTargetValueOnPercentualChange();
						break;
					case 'spaceAccessId':
						this.model.mainMetricSetupSpaceAccessId = value as number | null;
						break;
					case 'dimensions':
						this.model.mainMetricSetupDimensions = value as string[];
						break;
					case 'filters':
						this.model.mainMetricSetupFilters = value as DSSetup.MetricSetupFiltersData;
						break;
				}
				once(this, this.onMetricSetupChange); // bcos multiple props might be triggered at once
			},
		);

		const timeframe = this.model.timeframe;
		if (timeframe) {
			// we'll need periodEndTS for calculations
			this.isTimeframePeriodsLoading = true;
			void this.timeframeService.getTimeframePeriods(timeframe).then(() => {
				this.isTimeframePeriodsLoading = false;
			});
		}

		void this.model.loadMainMetricLimitations();

		next(() => {
			if (this.model.isNew) {
				this.model.setDefaultProgressAndFormatType(this.canUseAdvancedGoals);
				this.model.setDefaultDeliverySettings();
				this.model.checkSuccessCondition();
				this.model.checkPrimaryGranulation();

				if (this.model.isSubgoalsProgressGoal) {
					this.model.targetValueType = GoalTargetValueType.PERCENTAGE;
					this.model.measurement = String(GoalFormatType.PERCENTAGE);
					this.model.decimals = 2;
				}

				this.model.applyBufferedChanges();
			}

			if (this.model.isAutomaticGoal) {
				void this.fetchReferenceValue().then((referencePoint) => {
					this.referencePoint = referencePoint;
				});

				this.fetchForecastData();
			} else if (this.model.isManualGoal) {
				this.changeStartValueAndReferenceValue(this.model.startValue);
			}

			void this.requestGoal().then(() => {
				if (!this.model.isSubgoalsProgressGoal) {
					this.requestFormLayoutFillValueRowsAndRecalculate();
				}
			});
		});
	}

	get isEditDisabled() {
		return !this.model.isNew || this.submitInProgress;
	}

	get onlyAutomaticEnabled() {
		return !!this.popupData.onlyAutomaticEnabled;
	}

	get targetOtherMetric(): DSSetup.MetricSetupData | undefined {
		return this.model.isTargetOtherMetric
			? this.model.otherMetricSetup.metricSetupData || undefined
			: undefined;
	}

	get model() {
		return this.popupData.model;
	}

	get metricSetupThreeInSecondRow() {
		// which means first row only has metric select dropdown
		return (
			this.model.mainMetricSetup.allowBasicDimensionValueFilters &&
			this.model.mainMetricSetup.allowUnitNameFilter
		);
	}

	get goalSource() {
		return this.metricSetup.source;
	}

	get progressTypesList(): ProgressTypeItem[] {
		const items: ProgressTypeItem[] = [];

		if (!this.model.isObjectiveType) {
			items.push({
				value: GoalProgressType.AUTOMATIC,
				label: 'Metric',
				label2: 'Automatic',
				label2Icon: 'BoltFill',
				showUpgrade: !this.canUseAdvancedGoals,
				isDisabled: this.model.isObjectiveType,
				description:
					'Goal progress will be calculated automatically, from the progress of connected metric.',
			});
		}

		items.push(
			{
				value: GoalProgressType.MANUAL,
				label: 'Manual',
				isDisabled: this.onlyAutomaticEnabled,
				description: 'Progress of this goal is updated manually.',
			},
			{
				value: GoalProgressType.SUBGOALS,
				label: 'Sub-goals',
				label2: 'Automatic',
				label2Icon: 'BoltFill',
				isDisabled: this.isSubgoalsTypeDisabled,
				showAddon: !this.mmGoalsService.isGoalsOKRsEnabled,
				description:
					'Goal progress will be calculated automatically, from the progress of selected sub-goals.<br> You can add and remove sub-goals on the goal detail page.',
				tooltip: this.subGoalsTooltip,
			},
		);

		return this.model.isObjectiveType ? items.reverse() : items;
	}

	get progressTypeDescription() {
		return (
			this.progressTypesList.find(
				(progressType) => progressType.value === this.model.progressType,
			)?.description || ''
		);
	}

	get timeUnit() {
		return this.model.primaryGranulation;
	}

	get dateRangeCM() {
		const timeUnit = getGoalRangeTimeUnitForPrimaryGranulation(this.timeUnit);
		const dateRangeFormula: DateRangeInterface.FormulaInterface = {
			range: {
				includeFuture: true,
				timeUnit,
				multiplier: 1,
				offset: -1,
			},
			granulation: {
				isWholeRange: true,
			},
		};
		return DateRange.createFromFormula(dateRangeFormula);
	}

	get referenceValueText() {
		return this.model.isByEndDateAndNotContinuous
			? 'Previous period'
			: this.dateRangeCM.rangeTitleUX;
	}

	get goalCreatedDate(): DateRangeInterface.IdDate {
		const currentUserTimezone = this.currentUserService.model?.timezone;
		return getFormattedTime(
			this.model.created,
			currentUserTimezone,
			'YYYY-MM-DD',
		) as DateRangeInterface.IdDate;
	}

	get canUseAdvancedGoals(): boolean {
		return this.billingService.checkIfCan(BillingFeature.GOALS_ADVANCED);
	}

	get canUseGoalsForecasts(): boolean {
		return this.billingService.checkIfCan(BillingFeature.GOALS_FORECASTS);
	}

	get goalsForecastsLockedText(): string {
		return this.canUseGoalsForecasts ? '' : 'Unlock Metric forecasts';
	}

	get goalsForecastsText(): string {
		return this.canUseGoalsForecasts ? 'Forecast' : '';
	}

	get isReferenceValueLoading() {
		return !!this.mmGoalsService.isReferenceValueLoading;
	}

	get isForecastValueLoading() {
		return this.currentForecastRequestId !== null;
	}

	get isGoalLoading() {
		return (
			this.mmGoalsService.isSingleGoalRequestLoading ||
			this.submitInProgress ||
			this.isReferenceValueLoading ||
			this.isTimeframePeriodsLoading ||
			this.model.isMainMetricLimitationsLoading
		);
	}

	get isValidTargetValueEntered() {
		return typeof this.model.targetValue === 'number';
	}

	get isValueQueryLoading() {
		return this.isReferenceValueLoading || this.isForecastValueLoading;
	}

	get isHistoryValuesNotValid() {
		return !this.model.isHistoryValuesValid;
	}

	get targetEndDateTimezone() {
		return this.currentUserService.model?.timezone || 'Etc/UTC';
	}

	get isAutomaticGoalAndMetricIsSelected() {
		return this.model.isAutomaticGoal && this.model.isMetricFilled;
	}

	get isSubgoalsTypeDisabled() {
		return (
			this.onlyAutomaticEnabled ||
			!this.mmGoalsService.isGoalsOKRsEnabled ||
			this.model.isTimeframeContinuous ||
			!this.model.isPublic
		);
	}

	get showTargetSettings() {
		return this.model.isManualGoal || this.isAutomaticGoalAndMetricIsSelected;
	}

	get showAdvancedSettings() {
		return (
			this.showTargetSettings &&
			!this.model.isTargetOtherMetric &&
			!this.model.isSubgoalsProgressGoal &&
			!this.model.isMainMetricLimitationsLoading
		);
	}

	get isAdvancedSettingsDisabled() {
		return this.submitInProgress || this.isGoalLoading;
	}

	get firstDayOfTheWeekErrorText() {
		return this.isForecastFirstDayOfTheWeekError
			? this.mmForecastsService.firstDayOfTheWeekErrorText
			: null;
	}

	get subGoalsTooltip(): string | undefined {
		if (!this.model.isPublic) {
			return 'Sub-goals can only be added to public goals.';
		}

		if (!this.model.isTimeframeContinuous) {
			return undefined;
		}

		return "Sub-goals can't be selected as progress driver for goals with timeframe set to Continuous.";
	}

	private fetchReferenceValue() {
		return this.mmGoalsService.fetchReferenceValue(
			this.metricSetup,
			this.model.primaryGranulation,
			this.model.startDateTsFromTimeframe,
			this.model.endDateTsFromTimeframe,
		);
	}

	@tracked currentForecastRequestId: string | null = null;
	fetchForecastData() {
		if (!this.canUseGoalsForecasts) {
			return;
		}

		const timeUnit = this.timeUnit,
			dateRange = this.getDateRangeFormulaForMetricForecast(timeUnit);

		const metricSetup = this.metricSetup;

		if (!dateRange.isYesterday && metricSetup.key) {
			// if request already in progress, cancel subscription
			if (this.currentForecastRequestId) {
				this.mmForecastsService.unsubscribeFromInsightsForecastRequest(
					this.currentForecastRequestId,
				);
			}

			if (timeUnit === TimeUnit.DAY) {
				return;
			}

			const requestId = this.mmForecastsService.requestInsightsForecastData(
				dateRange,
				metricSetup,
				(forecastData) => {
					if (requestId !== this.currentForecastRequestId) {
						return true; // unsubscribe, this is an outdated request
					}

					this.setForecastData({
						forecastData,
					});
					this.currentForecastRequestId = null;
					return true; // unsubscribe after handling
				},
				(errorData) => {
					if (requestId !== this.currentForecastRequestId) {
						return;
					}

					const isFirstDayOfTheWeekError =
						this.mmForecastsService.getIsTheFirstDayOfTheWeekError(errorData);

					this.setForecastData({
						forecastData: null,
						isFirstDayOfTheWeekError,
					});

					this.currentForecastRequestId = null;
				},
			);

			this.currentForecastRequestId = requestId;
		}
	}

	private setForecastData(data: {
		forecastData: APIData.MetricInsights.ForecastDataResponse | null;
		isFirstDayOfTheWeekError?: boolean;
	}) {
		this.forecastData = data.forecastData;
		this.isForecastFirstDayOfTheWeekError = !!data.isFirstDayOfTheWeekError;
	}

	@action
	changeStartValue(value: number | string | null) {
		this.changeStartValueAndReferenceValue(value);
		this.recalculateAllValues();
	}

	@action
	changeStartValueAndReferenceValue(value: number | string | null) {
		this.setGoalStartValue(value);
		this.setReferencePoint(value);
	}

	@action
	changeCurrency(currency: string) {
		this.model.formatUnit = currency;
	}

	@action
	changeMetricSelection(value: DSSetup.MetricData | null) {
		this.metricSetup.setFromMetricData(value);
		this.setOptimalUnit();
	}

	private onMetricSetupChange() {
		void this.model.loadMainMetricLimitations();

		this.checkToSetDefaultTargetValueOnPercentualChange();

		this.model.checkSuccessCondition();

		if (this.model.isAutomaticGoal) {
			void this.fetchReferenceValue().then((referencePoint) => {
				this.referencePoint = referencePoint;

				this.checkAndRecalculateAllValues(true);
			});
		}

		this.fetchForecastData();
		this.scheduleReposition();
	}

	@action
	changeDimensions(value: string[]) {
		this.metricSetup.dimensions = value;
		this.metricSetup.resetFilters();
		this.setOptimalUnit();
	}

	@action
	changeAttributeName(value: string | null) {
		this.metricSetup.setFiltersFromAttributeName(value);
		this.setOptimalUnit();
	}

	@action
	changeUnitName(value: string | null) {
		this.metricSetup.unitName = value;
	}

	@action
	changeTargetRange(
		targetValueType: GoalTargetValueType,
		primaryGranulation: GoalRangeTimeUnit,
		otherMetric?: DSSetup.MetricSetupData,
	) {
		const currentPrimaryGranulation = this.model.primaryGranulation;

		this.model.targetValueType = targetValueType;
		this.model.primaryGranulation = primaryGranulation;
		this.model.setDefaultScheduleTypeOnPrimaryGranulationChange();

		if (otherMetric) {
			this.model.otherMetricSetup.setFromMetricSetupData(otherMetric);
			this.model.historyValues = {};
			this.model.targetValue = null;
		} else {
			this.model.otherMetricSetup.setFromMetricSetupData(null);
		}

		if (currentPrimaryGranulation !== primaryGranulation) {
			void this.fetchReferenceValue().then((referencePoint) => {
				this.referencePoint = referencePoint;
			});
		}

		this.checkToSetDefaultTargetValueOnPercentualChange();
		this.checkAndRecalculateAllValues(true);
		this.fetchForecastData();
	}

	@action
	goalSaved(): void {
		if (this.popupData.onGoalSaved) {
			this.popupData.onGoalSaved(this.model); // for metric edit
		}
	}

	@action
	changeDecimals(value: number): void {
		this.model.decimals = value;
	}

	@action
	changeProgressType(progressType: AnySimpleValue): void {
		if (this.model.progressType === progressType) {
			return;
		}

		const isNewProgressTypeMetric = progressType === GoalProgressType.AUTOMATIC;

		// different for manual vs others
		const isPreviousProgressTypeManual = this.model.progressType === GoalProgressType.MANUAL;
		const isNewProgressTypeManual = progressType === GoalProgressType.MANUAL;
		const resetDeliverySettings = isPreviousProgressTypeManual !== isNewProgressTypeManual;

		if (
			isNewProgressTypeMetric &&
			!this.billingService.checkIfCan(BillingFeature.GOALS_ADVANCED, true)
		) {
			return;
		}

		this.model.progressType = progressType as GoalProgressType;
		this.model.formatType = this.model.isManualGoal ? GoalFormatType.PERCENTAGE : null;
		this.model.historyValues = {};
		this.model.checkSuccessCondition();
		this.model.checkPrimaryGranulation();

		if (resetDeliverySettings) {
			this.model.setDefaultDeliverySettings();
		}

		if (isNewProgressTypeMetric) {
			this.startValue = null;
			this.model.startValue = null;
			this.referencePoint = null;
			this.model.targetValueType = GoalTargetValueType.FIXED;
			this.checkToSetDefaultTargetValueOnPercentualChange();
			this.recalculateAllValues();
		} else if (this.model.isManualGoal) {
			this.model.formatUnit = null;
			this.model.targetValueType = GoalTargetValueType.FIXED; // manual goals can only have fixed target value

			this.checkToSetDefaultTargetValueOnPercentualChange();
			this.changeStartValueAndReferenceValue(this.startValue);
			this.changeMetricSelection(null);

			if (this.model.isGoalType) {
				this.recalculateAllValues();
			} else {
				this.requestFormLayoutFillValueRowsAndRecalculate();
			}
		} else if (this.model.isSubgoalsProgressGoal) {
			this.startValue = null;
			this.model.startValue = null;
			this.referencePoint = null;
			this.model.targetValueType = GoalTargetValueType.PERCENTAGE;
			this.model.measurement = String(GoalFormatType.PERCENTAGE);
			this.model.decimals = 2;
			this.model.historyValues = {};
		}

		this.scheduleReposition();
	}

	@action
	changeSuccessCondition(value: GoalTargetSuccessCondition) {
		if (this.model.targetSuccessCondition === value) {
			return;
		}

		this.model.targetSuccessCondition = value;

		this.model.checkPrimaryGranulation();
		void this.fetchReferenceValue().then((referencePoint) => {
			this.referencePoint = referencePoint;
		});

		this.checkAndRecalculateAllValues(true);
	}

	@action
	checkToSetDefaultTargetValueOnPercentualChange() {
		if (this.hasTargetValueChanged || !this.model.isNew || this.model.isTargetOtherMetric) {
			return;
		}
		// set target value on model
		const isValuesPercentual =
			this.model.isHistoryValuesPercentFormat || this.model.isTargetValuePercentFormat;

		const value =
			this.model.isManualGoal && isValuesPercentual
				? 100
				: this.mmGoalsService.getDefaultTargetValue(isValuesPercentual);
		this.model.targetValue = value;
	}

	@action
	checkIsDirty(): boolean {
		return this.model.isDirty;
	}

	@action
	save(): void {
		if (!this.model.validate()) {
			return;
		}

		// TODO: @tadcom0: disable for now
		// if (this.model.isAnyHistoryValueTooBig) {
		// 	this.infolayerService.error(
		// 		'Goal period target values are too big. Please set a smaller target value.',
		// 	);
		// 	return;
		// }

		this.checkAndRecalculateAllValues();

		if (this.isHistoryValuesNotValid) {
			this.infolayerService.error(
				'Goal period target values are missing. Please open the advanced settings to set target values for each period.',
			);

			return;
		}

		this.submitInProgress = true;

		const isModelNew = this.model.isNew;

		this.model
			.save()
			.then(() => {
				this.infolayerService.ok('Goal saved!');

				const eventData: APIData.Tracking.Data = {
					title: this.model.title,
					type: this.model.isManualGoal
						? 'manual'
						: this.model.isSubgoalsProgressGoal
							? 'subgoals'
							: 'automatic',
					targetType: this.model.isTargetValuePercentFormat ? 'percent' : 'fixed',
					notification: this.mmGoalsService.getScheduleTypeArrayOfString(this.model),
					origin: this.popupData.origin,
					entityType: this.model.type === GoalType.GOAL ? 'goal' : 'objective',
					timeframe: this.timeframeService.getTimeframeTypeStringForTracking(
						this.model.timeframe?.type,
					),
					accountableTeam: this.model.groupId ? 'team' : 'company',
					successCondition: this.mmGoalsService.getTargetSuccessConditionString(
						this.model.targetSuccessCondition,
					),
				};

				if (isModelNew) {
					const datasource = this.model.mainMetricSetup.sourceType?.key,
						metricName = this.model.mainMetricSetup.metric?.name;

					if (datasource) {
						eventData.datasource = datasource;
					}

					if (metricName) {
						eventData.metrics = [metricName];
					}
				}

				const trackingEvent = isModelNew
					? TrackingEvent.GOAL_CREATED
					: TrackingEvent.GOAL_EDITED;

				this.analyticsService.event(trackingEvent, eventData);

				this.goalSaved();

				if (isModelNew && this.model.isSubgoalsProgressGoal) {
					this.mmGoalsService.openGoalInsightsPage(this.model);
				}

				this.close();
			})
			.finally(() => {
				this.submitInProgress = false;
			})
			.catch((errorData: RequestError) => {
				this.requestService.showAllValidationErrors(errorData);
			});
	}

	@action
	changeTargetValue(value: number | null) {
		if (this.model.targetValue === value) {
			return;
		}

		this.hasTargetValueChanged = true;
		this.model.targetValue = value;

		this.checkAndRecalculateAllValues(true);
	}

	@action
	changeNotifications() {
		this.areNotificationsEnabled = !this.areNotificationsEnabled;
	}

	@action
	openAdvancedSetup() {
		this.model.errors = {}; // reset current errors

		const validateFields = ['targetValue'];

		if (this.model.isAutomaticGoal) {
			validateFields.push('mainMetricSetupKey');
		}

		if (!this.model.validate(validateFields)) {
			return;
		}

		const changeReferencePoint = (referencePoint: DChartPrimaryPoint | null) => {
			this.referencePoint = referencePoint;
		};

		this.mmGoalsService.openAdvancedSetup(
			this.model,
			this.metricSetup,
			this.referencePoint,
			this.popupData.origin,
			changeReferencePoint,
		);
	}

	@action
	goToFirstStep() {
		if (!this.model.isNew) {
			return;
		}

		this.mmGoalsService.openCreatePopup(
			this.model,
			this.popupData.origin,
			this.popupData.onGoalSaved,
			this.popupData.isGoalTypeObjectiveDisabled,
			this.popupData.isSubgoal,
		);

		void this.popupService.close(this.args.model);
	}

	// ------- Notifications start ------

	get ownerScheduleType() {
		return this.model.ownerScheduleType ?? GoalOwnerScheduleType.MONTHLY;
	}

	@action
	toggleIsDeliveryOn() {
		this.model.isDeliveryOn = !this.model.isDeliveryOn;
	}

	@action
	changeOwnerScheduleType(scheduleType: GoalOwnerScheduleType) {
		this.model.ownerScheduleType = scheduleType;
	}

	// ------- Notifications end ------

	@tracked private referencePoint: DChartPrimaryPoint | null = null;
	@tracked valueRows: GoalValueRow[] = [];

	@cached
	get valueRowsByTimeUnit() {
		return groupValueRowsByTimeUnit(this.valueRows);
	}

	private recalculateAllValues(skipMetricLimitationsLoad = false) {
		if (
			!skipMetricLimitationsLoad &&
			this.model.willScheduleAfterMainMetricLimitationsLoaded(() =>
				this.recalculateAllValues(true),
			)
		) {
			return;
		}

		recalculateAllValues(
			this.valueRowsByTimeUnit,
			this.model.primaryGranulation,
			this.model.isTargetValuePercentFormat, // referencePoint required
			this.model.targetValue!,
			this.model.startDateTsFromTimeframe,
			this.model.valuesLastDateTs,
			this.model.useEndDateForCalculations, // referencePoint required if true
			this.model.mainMetricType,
			this.referencePoint?.value,
		);

		this.valueRows.forEach((r) => {
			r.markClean();
		});

		this.mmGoalsService.markRowHiddenAndClearValue(this.valueRows, this.model.valuesLastDateTs);

		this.model.historyValues = getHistoryValuesFromValueRows(this.valueRowsByTimeUnit);
	}

	private checkAndRecalculateAllValues(forceRecalculate = false) {
		if (this.model.isSubgoalsProgressGoal) {
			this.model.historyValues = {};
			return;
		}

		if (this.isTimeframePeriodsLoading && !this.model.endDate) {
			return;
		}

		const shouldRecalculateAllValues = getShouldRecalculateAllValues(
			this.isValidTargetValueEntered,
			this.model.historyValuesOverride,
			this.isHistoryValuesNotValid,
		);

		if (!this.model.isTargetOtherMetric && (shouldRecalculateAllValues || forceRecalculate)) {
			this.recalculateAllValues();
		}
	}

	private getDateRangeFormulaForMetricForecast(timeUnit: GoalRangeTimeUnit) {
		const isCumulative = timeUnit !== TimeUnit.WEEK;

		const granulation = {
			cumulative: isCumulative,
			isWholeRange: false,
		};

		let range: DateRangeInterface.RangeInterface = {
			timeUnit,
			includeFuture: true,
			multiplier: 1,
			offset: 0,
		};

		if (timeUnit === TimeUnit.TOTAL) {
			// Total is by end date
			range = {
				startDate: this.goalCreatedDate,
				endDate: this.model.valuesLastDate as DateRangeInterface.IdDate,
				includeFuture: true,
			};
		}

		return DateRange.createFromFormula({
			granulation,
			range,
		});
	}

	private setGoalStartValue(value?: number | string | null) {
		const stringValueForInput = this.model.setStartValueFromUserInput(value);
		this.startValue = stringValueForInput;
	}

	private setReferencePoint(value?: number | string | null) {
		if (this.mmGoalsService.isValueValid(value)) {
			const pointData = {
				value: Number(value),
				valueStr: String(value),
			};

			this.referencePoint = new DChartPrimaryPoint(pointData);
		} else {
			this.referencePoint = null;
		}
	}

	@action
	destroyView(): void {
		if (this.currentForecastRequestId) {
			this.mmForecastsService.unsubscribeFromInsightsForecastRequest(
				this.currentForecastRequestId,
			);
		}

		const referenceValuePromise = this.mmGoalsService._fetchReferenceValuePromise;
		if (referenceValuePromise) {
			(referenceValuePromise.jqPromise as RSjqXHR | undefined)?.abort();
		}
	}

	private setOptimalUnit(): void {
		const metricSetup = this.metricSetup;
		if (!metricSetup.hasUnits || metricSetup.specificSelectedUnit) {
			return;
		}

		const metricData = metricSetup.metricData;
		if (!metricData) {
			return;
		}

		const prevKey = metricSetup.key,
			prevUnit = metricSetup.unitName;

		void this.mmMetricsService
			.getOptimalUnit(metricData)
			.then((unit) => {
				if (prevKey !== metricSetup.key) {
					return;
				}

				if (prevUnit !== metricSetup.unitName) {
					return;
				}

				this.changeUnitName(unit);
			})
			.catch();
	}

	private requestGoal(): Promise<void> {
		if (this.isHistoryValuesNotValid && !this.model.isNew) {
			return this.mmGoalsService.requestSingleGoal(this.model.id!);
		} else {
			return Promise.resolve();
		}
	}

	private requestFormLayoutFillValueRowsAndRecalculate() {
		const timeframe = this.model.timeframe;
		const timeframePeriodsPromise =
			timeframe && !this.model.endDate
				? this.timeframeService.getTimeframePeriods(timeframe)
				: Promise.resolve();

		void timeframePeriodsPromise.then(() => {
			void this.mmGoalsService.requestFullFormLayout(this.model).then(() => {
				this.valueRows = this.mmGoalsService.updateFormLayout(
					this.valueRows,
					this.valueRowsByTimeUnit,
					this.model.isMainMetricPercentage,
					this.model.isMainMetricInverted,
				); // gets form layout and saves it to valueRows
				if (!this.model.isNew) {
					this.valueRows = fillValueRowsFromHistoryValues(
						// gets history values and fills form layout with them
						this.model.historyValues,
						this.valueRows,
					);
				}

				if (this.model.isNew) {
					this.checkAndRecalculateAllValues(true); // recalculates all values and saves them in form layout
				}
			});
		});
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="Preferences" @size="lg" />
			Progress settings for '{{this.model.title}}'

		</div>

		<div class="content" {{willDestroy this.destroyView}}>
			<div class="form-column">
				<span class="label">Progress source</span>
				<FormRadiobuttonBoxGroupComponent
					@options={{this.progressTypesList}}
					@selected={{this.model.progressType}}
					@onChange={{this.changeProgressType}}
					@isDisabled={{this.isEditDisabled}}
					@extraClass="form-row"
				/>
				<p class="description">{{{this.progressTypeDescription}}}</p>
			</div>
			<div class="white-ct">
				{{#if this.model.isAutomaticGoal}}
					<div class="form-row">
						<div class="form-column">
							<span class="label">Metric</span>
							<FormMetricSelectComponent
								@selected={{this.metricSetup.metricData}}
								@onChange={{this.changeMetricSelection}}
								@disableNotConnected={{true}}
								@placeholder="Select metric"
								@isDisabled={{this.isEditDisabled}}
								@skipGoals={{true}}
								@size="big"
							/>
							<RowErrorComponent @items={{this.model.errors.mainMetricSetupKey}} />
						</div>
						<div
							class="form-row
								{{if this.metricSetupThreeInSecondRow 'form-row--three-columns'}}"
						>
							{{#if this.metricSetup.allowDimensions}}
								<div class="metric-dimension">
									<FormSelectDimensionsComponent
										@metricSetup={{this.metricSetup}}
										@isDisabled={{this.isEditDisabled}}
										@onChange={{this.changeDimensions}}
										@size="big"
									/>
								</div>
							{{/if}}
							{{#if this.metricSetup.allowBasicDimensionValueFilters}}
								<div class="metric-attribute">
									<FormSelectAttributesComponent
										@selected={{this.metricSetup.firstAttributeNameFromBasicFilters}}
										@metricSetupForQuery={{this.metricSetup}}
										@onChange={{this.changeAttributeName}}
										@isDisabled={{this.isEditDisabled}}
										@isRequired={{true}}
										@size="big"
									/>
									<RowErrorComponent
										@items={{this.model.errors.mainMetricAttributeName}}
									/>
								</div>
							{{/if}}
							{{#if this.metricSetup.allowUnitNameFilter}}
								<div class="metric-unit">
									<FormSelectUnitsComponent
										@selected={{this.metricSetup.unitName}}
										@metricSetupForQuery={{this.metricSetup}}
										@dateRangeForQuery={{this.model.dateRange}}
										@onChange={{this.changeUnitName}}
										@isDisabled={{this.isEditDisabled}}
										@allowAllUnits={{false}}
										@isMultiple={{false}}
										@size="big"
									/>
									<RowErrorComponent
										@items={{this.model.errors.mainMetricUnitName}}
									/>
								</div>
							{{/if}}
						</div>
					</div>
				{{/if}}
				{{#if this.showTargetSettings}}
					{{#if this.model.isManualGoal}}
						<div class="form-row">
							<div class="form-column manual-input">
								<span class="label">Starting value</span>
								<GoalsStartingValueComponent
									@model={{this.model}}
									@value={{this.startValue}}
									@onChange={{this.changeStartValue}}
									@isDisabled={{this.submitInProgress}}
								/>
							</div>
							<div class="form-column"></div>
						</div>
					{{/if}}
					<div class="form-row">
						{{#unless this.model.isSubgoalsProgressGoal}}
							<div class="form-column">
								<span class="label">Success condition</span>
								<GoalsSuccessConditionComponent
									@model={{this.model}}
									@submitInProgress={{this.submitInProgress}}
									@onChange={{this.changeSuccessCondition}}
								/>
							</div>
						{{/unless}}
						<div class="form-column">
							<span class="label">Target value</span>
							<GoalsTargetTypeAndGranulationDropdownComponent
								@model={{this.model}}
								@targetOtherMetric={{this.targetOtherMetric}}
								@changeTargetValue={{this.changeTargetValue}}
								@changeTargetRange={{this.changeTargetRange}}
								@isDisabled={{this.submitInProgress}}
							/>
							<RowErrorComponent @items={{this.model.errors.targetValue}} />
						</div>
					</div>
				{{/if}}
				{{#if this.isAutomaticGoalAndMetricIsSelected}}
					<div class="value-query">
						{{#unless this.isReferenceValueLoading}}
							<GoalValueQueryComponent
								@text={{this.referenceValueText}}
								@valueString={{this.referencePoint.valueStr}}
							/>
						{{/unless}}
						{{#unless this.isForecastValueLoading}}
							<GoalValueQueryComponent
								@text={{this.goalsForecastsText}}
								@valueString={{this.forecastData.forecastValueStr}}
								@changeString={{this.forecastData.changeString}}
								@inverted={{this.model.isMainMetricInverted}}
								@metricSetupData={{this.metricSetup.metricSetupWithoutFiltersData}}
								@path="/forecasts/explorer"
								@tooltipText={{this.firstDayOfTheWeekErrorText}}
								@lockedText={{this.goalsForecastsLockedText}}
							/>
						{{/unless}}
						{{#if this.isValueQueryLoading}}
							<div class="loader">
								<BarLoaderComponent @size="tiny" />
								Gathering data...
							</div>
						{{/if}}
					</div>
				{{/if}}

				{{#if this.model.isSubgoalsProgressGoal}}
					<div class="form-row">
						<div class="form-column">
							<span class="label">Measurement</span>
							<div class="select-dropdown">
								<GoalsMeasurementDropdownComponent @model={{this.model}} />
							</div>
						</div>
						<div class="form-column">
							<span class="label">Formatting</span>
							<div class="select-dropdown">
								<FormSelectComponent
									name="decimals"
									@selected={{this.model.decimals}}
									@options={{this.decimalsOptions}}
									@onChange={{this.changeDecimals}}
									@showFloatlabel={{false}}
									@isDisabled={{true}}
									@showLabel2={{false}}
									@isRequired={{true}}
								/>
								<ToolTipComponent
									@text="Formatting not supported yet"
									@position="top"
								/>
							</div>
						</div>
					</div>
				{{/if}}

				{{#if this.showAdvancedSettings}}
					<div class="advanced-period {{if this.isAdvancedSettingsDisabled 'disabled'}}">
						<CommonUiIconComponent @name="Calendar" @size="md" />
						<a role="button" {{on "click" this.openAdvancedSetup}}>
							Advanced period settings
						</a>
					</div>
				{{/if}}
			</div>
			<div class="form-row notifications">
				<div class="form-column">
					<span class="label">Status update</span>
					{{#if this.model.isManualGoal}}
						<GoalNotificationsOwnerComponent
							@isDeliveryOn={{this.model.isDeliveryOn}}
							@scheduleType={{this.ownerScheduleType}}
							@submitInProgress={{this.submitInProgress}}
							@optionsAsDropdown={{true}}
							@toggleDelivery={{this.toggleIsDeliveryOn}}
							@changeScheduleType={{this.changeOwnerScheduleType}}
						/>
					{{else}}
						<GoalNotificationsSettingsComponent
							@model={{this.model}}
							@toggleDelivery={{this.toggleIsDeliveryOn}}
							@submitInProgress={{this.submitInProgress}}
							@isPopupOpen={{this.popupData.isNotificationsPopupOpen}}
						/>
					{{/if}}
				</div>
			</div>
			<div class="controls2">
				<div class="controls2__left">
					{{#if this.model.isNew}}
						<SubmitButtonComponent
							@text="Back"
							@theme="grey-empty"
							@onTrigger={{this.goToFirstStep}}
							@icon="ChevronLeft"
						/>
					{{/if}}
				</div>
				<div class="controls2__right">
					{{#unless this.model.isNew}}
						<SubmitButtonComponent
							@text="Cancel"
							@theme="grey-empty"
							@onTrigger={{this.close}}
						/>
					{{/unless}}
					<FormLeaveConfirmPopupComponent
						@popup={{@model}}
						@save={{this.save}}
						@checkIsDirty={{this.checkIsDirty}}
						@slideFromRight={{true}}
						as |controls|
					>
						<controls.save
							@text="Save"
							@extraClass="save"
							@isDisabled={{this.isGoalLoading}}
							@submitInProgress={{this.submitInProgress}}
						/>
					</FormLeaveConfirmPopupComponent>
				</div>
			</div>
		</div>
	</template>
}
